import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Task } from '../../services/taskService';
import { Tag } from '../../services/tagService';
import TagDisplay from './TagDisplay';
import './TaskRow.css';

interface TaskRowProps {
  task: Task;
  onToggleStatus?: (task: Task) => void;
  onTaskClick?: (taskId: number) => void;
  onMenuAction?: (task: Task, action: 'today' | 'tomorrow' | 'next-week' | 'backlog' | 'custom-date' | 'delete' | 'log-time') => void;
  taskTags?: string[];
  availableTags?: Tag[];
  showMenu?: boolean;
  showStatusToggle?: boolean;
  isHighlighted?: boolean;
  onToggleHighlight?: (task: Task) => void;
  projectName?: string;
}

const TaskRow: React.FC<TaskRowProps> = ({
  task,
  onToggleStatus,
  onTaskClick,
  onMenuAction,
  taskTags = [],
  availableTags = [],
  showMenu = true,
  showStatusToggle = true,
  isHighlighted = false,
  onToggleHighlight,
  projectName
}) => {
  const [openMenu, setOpenMenu] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; placement: 'top' | 'bottom' } | null>(null);

  // Handle clicks outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const menuPopups = document.querySelectorAll('.task-row-menu-popup, .task-menu-popup');
      let clickedInsideMenu = false;

      menuPopups.forEach((popup) => {
        if (popup.contains(target)) {
          clickedInsideMenu = true;
        }
      });

      const menuButtons = document.querySelectorAll('.task-row-menu-btn, .task-menu-btn');
      let clickedOnMenuButton = false;

      menuButtons.forEach((button) => {
        if (button.contains(target)) {
          clickedOnMenuButton = true;
        }
      });

      const menuOptions = document.querySelectorAll('.task-menu-option');
      let clickedOnMenuOption = false;

      menuOptions.forEach((option) => {
        if (option.contains(target)) {
          clickedOnMenuOption = true;
        }
      });

      if (!clickedInsideMenu && !clickedOnMenuButton && !clickedOnMenuOption) {
        setOpenMenu(false);
        setMenuPosition(null);
      }
    };

    if (openMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenu]);

  // Update menu position on scroll/resize when menu is open
  useEffect(() => {
    if (!openMenu || !menuButtonRef.current) return;

    const updatePosition = () => {
      if (menuButtonRef.current) {
        const rect = menuButtonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const menuHeightEstimate = 300; // Approximate max height of menu

        if (spaceBelow < menuHeightEstimate) {
          // Position above
          setMenuPosition({
            top: rect.top + window.scrollY - 4,
            left: rect.right + window.scrollX,
            placement: 'top'
          });
        } else {
          // Position below
          setMenuPosition({
            top: rect.bottom + window.scrollY + 4,
            left: rect.right + window.scrollX,
            placement: 'bottom'
          });
        }
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    // Initial update
    updatePosition();

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [openMenu]);

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Logic moved to useEffect, just toggle here
    setOpenMenu(!openMenu);
  };


  const handleMenuAction = (e: React.MouseEvent, action: 'today' | 'tomorrow' | 'next-week' | 'backlog' | 'custom-date' | 'delete' | 'highlight' | 'log-time') => {
    e.preventDefault();
    e.stopPropagation();
    
    // Execute callback first before closing menu
    if (action === 'highlight') {
      if (onToggleHighlight) {
        onToggleHighlight(task);
      }
      setOpenMenu(false);
      setMenuPosition(null);
      return;
    }

    if (onMenuAction) {
      // Call the callback first
      onMenuAction(task, action);
    }
    
    // Close menu after callback is called
    setOpenMenu(false);
    setMenuPosition(null);
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleStatus) {
      onToggleStatus(task);
    }
  };

  const handleTaskClick = () => {
    if (onTaskClick) {
      onTaskClick(task.id);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}m`;
  };

  return (
    <div className="task-item-container">
      <div className={`task-item ${task.status === 'completed' ? 'completed' : ''} ${isHighlighted ? 'highlighted' : ''}`}>
        {showStatusToggle && (
          <button
            className={`task-checkbox ${task.status === 'completed' ? 'completed' : ''}`}
            onClick={handleToggleClick}
          >
            <span className="task-checkbox-mark">{task.status === 'completed' ? '✓' : ''}</span>
          </button>
        )}
        <div className="task-name-container">
          <span
            className="task-name clickable"
            onClick={handleTaskClick}
            title="Click to view task details"
          >
            {task.title}
          </span>
          {task.goals && task.goals.length > 0 && (
            <div className="task-goal-names">
              {task.goals.map((goal, index) => (
                <span key={goal.id} className="task-goal-name">
                  {index > 0 && ', '}
                  → {goal.title}
                </span>
              ))}
            </div>
          )}
          <div className="task-meta-container">
            {taskTags.length > 0 && (
              <TagDisplay
                tags={taskTags}
                availableTags={availableTags}
              />
            )}
            {projectName && (
              <span className="task-project-name">{projectName}</span>
            )}
            {task.assigned_date && (
              <span className="task-assigned-date">{formatDate(task.assigned_date)}</span>
            )}
            {task.total_minutes && task.total_minutes > 0 && (
              <span className="task-time-logged" title="Total time logged">
                ⏱ {formatDuration(task.total_minutes)}
              </span>
            )}
          </div>
        </div>
        {showMenu && (
          <button
            ref={menuButtonRef}
            className="task-row-menu-btn task-menu-btn"
            onClick={handleMenuClick}
            title="Task options"
          >
            ⋯
          </button>
        )}
      </div>
      {openMenu && menuPosition && showMenu && createPortal(
        <div
          className="task-row-menu-popup task-menu-popup portal"
          style={{
            position: 'absolute',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            transform: menuPosition.placement === 'top' ? 'translate(-100%, -100%)' : 'translate(-100%, 0)',
            zIndex: 1000
          }}
        >
          <button
            className="task-menu-option"
            onClick={(e) => handleMenuAction(e, 'today')}
          >
            Today
          </button>
          <button
            className="task-menu-option"
            onClick={(e) => handleMenuAction(e, 'tomorrow')}
          >
            Tomorrow
          </button>
          <button
            className="task-menu-option"
            onClick={(e) => handleMenuAction(e, 'next-week')}
          >
            Next week
          </button>
          <button
            className="task-menu-option"
            onClick={(e) => handleMenuAction(e, 'backlog')}
          >
            To backlog
          </button>
          <button
            className="task-menu-option"
            onClick={(e) => handleMenuAction(e, 'custom-date')}
          >
            Move to date...
          </button>
          {onToggleHighlight && (
            <button
              className="task-menu-option"
              onClick={(e) => handleMenuAction(e, 'highlight')}
            >
              {isHighlighted ? 'Unhighlight' : 'Highlight'}
            </button>
          )}
          <button
            className="task-menu-option"
            onClick={(e) => handleMenuAction(e, 'log-time')}
          >
            Log Time
          </button>
          <div className="task-menu-separator"></div>
          <button
            className="task-menu-option delete"
            onClick={(e) => handleMenuAction(e, 'delete')}
          >
            Delete
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TaskRow;

